import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ReactComponent as CheckMarkIcon } from 'assets/icons/check_circle_no_fill.svg';
import { ReactComponent as CrownIcon } from 'assets/icons/crown_upgrade_fill.svg';
import { MAX_TEXT_GENERATION_CREDITS } from 'constants/editor';
import { UserTypes } from 'modules/User';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { handleTranslationText } from './CommonGatedUpgradeModal.helpers';
import {
  CommonGatedUpgradeModalContentProps,
  CommonGatedUpgradeModalProps,
  ContentProps,
  CustomTextProps,
  DescriptionProps,
  DescriptionWithIconProps,
  HeaderProps,
  IconProps,
  ModalUpgradeTypeEnum,
  SubheaderProps,
  UpgradeButtonProps,
  UpgradeDescription,
} from './CommonGatedUpgradeModal.types';

export const commonUpgradeModalId = 'common-upgrade-modal-id';
export const CommonGatedUpgradeModal = memo(
  ({
    isOpen,
    onClose,
    onClickUpgrade,
    upgradePlan,
    modalImg,
    modalType,
    modalContentProps,
  }: CommonGatedUpgradeModalProps) => {
    return (
      <Modal
        id={commonUpgradeModalId}
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        closeOnEsc
        closeOnOverlayClick
        returnFocusOnClose={false}
      >
        {/* define addition zIndex if a modal needs to be on top of a popover, etc. */}
        <ModalOverlay zIndex={modalContentProps ? modalContentProps.zIndex : 'modal'} />
        <CommonGatedUpgradeModalContent
          onClickUpgrade={onClickUpgrade}
          upgradePlan={upgradePlan}
          modalImg={modalImg}
          modalType={modalType}
          modalContentProps={modalContentProps}
        />
      </Modal>
    );
  },
);

CommonGatedUpgradeModal.displayName = 'CommonGatedUpgradeModal';

const CommonGatedUpgradeModalContent = ({
  onClickUpgrade,
  upgradePlan,
  modalImg,
  modalType,
  modalContentProps,
}: CommonGatedUpgradeModalContentProps) => {
  const isUpgradingToPremium = upgradePlan === UserTypes.Premium;
  const isUpgradingToBusiness = upgradePlan === UserTypes.Business;

  return (
    <ModalContent minW={'44rem'} containerProps={modalContentProps}>
      <ModalBody padding={'unset'}>
        <Flex flexDir={'row'}>
          {/* alt text none to be marked as decorative image */}
          <Image
            src={modalImg}
            h={'29.5rem'}
            w={'full'}
            minW={'340px'}
            borderTopLeftRadius={'md'}
            borderBottomLeftRadius={'md'}
            alt=''
            role='presentation'
          />
          <Flex minW={'22rem'} flexDirection={'column'} padding={'2rem 1.5rem'} gap={'1rem'}>
            {isUpgradingToPremium && (
              <PremiumContent onClickUpgrade={onClickUpgrade} upgradePlan={upgradePlan} modalType={modalType} />
            )}
            {isUpgradingToBusiness && (
              <BusinessContent onClickUpgrade={onClickUpgrade} upgradePlan={upgradePlan} modalType={modalType} />
            )}
          </Flex>
        </Flex>
      </ModalBody>
      <ModalCloseButton
        position={'absolute'}
        background={'white'}
        borderRadius={'9999px'}
        right={'-2.5rem'}
        top={'0'}
        _hover={{ bg: 'white-alpha.500' }}
        data-testid={'icon-upgrade-close-button'}
      />
    </ModalContent>
  );
};

const PremiumContent = ({ onClickUpgrade, upgradePlan, modalType }: ContentProps) => {
  return (
    <>
      <Header upgradePlan={upgradePlan} modalType={modalType} />
      <Subheader upgradePlan={upgradePlan} modalType={modalType} />
      <PremiumUpgradeDescriptions modalType={modalType} />
      <UpgradeButton upgradePlan={upgradePlan} onClickUpgrade={onClickUpgrade} modalType={modalType} />
    </>
  );
};

const PremiumUpgradeDescriptions = ({ modalType }: UpgradeDescription) => {
  const { t } = useTranslation('editor_modals', {
    useSuspense: false,
    keyPrefix: handleTranslationText(modalType),
  });

  return (
    <VStack alignItems={'baseline'} as={'ul'}>
      <DescriptionWithIcon color={'var(--vg-colors-avatar-3)'}>
        {t('general.description.first.partOne')} <b>{t('general.description.first.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-avatar-3)'}>
        {t('premium.description.second.partOne')} <b>{t('premium.description.second.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-avatar-3)'}>
        {t('general.description.third.partOne')} <b>{t('premium.description.third.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-avatar-3)'}>
        {t('general.description.fourth.partOne')} <b>50 {t('general.description.fourth.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-avatar-3)'}>
        <b>{t('premium.description.fifth.partOne')}</b> {t('premium.description.fifth.partTwo')}
      </DescriptionWithIcon>
    </VStack>
  );
};

const BusinessContent = ({ onClickUpgrade, upgradePlan, modalType }: ContentProps) => {
  return (
    <>
      <Header upgradePlan={upgradePlan} modalType={modalType} />
      <Subheader upgradePlan={upgradePlan} modalType={modalType} />
      <BusinessUpgradeDescriptions modalType={modalType} />
      <UpgradeButton upgradePlan={upgradePlan} onClickUpgrade={onClickUpgrade} modalType={modalType} />
    </>
  );
};

const BusinessUpgradeDescriptions = ({ modalType }: UpgradeDescription) => {
  const { t } = useTranslation('editor_modals', {
    useSuspense: false,
    keyPrefix: handleTranslationText(modalType),
  });

  return (
    <VStack alignItems={'baseline'} as={'ul'}>
      <DescriptionWithIcon color={'var(--vg-colors-upgrade-blue-500)'}>
        {t('general.description.first.partOne')} <b>{t('general.description.first.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-upgrade-blue-500)'}>
        {t('business.description.second.partOne')} <b>{t('business.description.second.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-upgrade-blue-500)'}>
        {t('general.description.third.partOne')} <b>{t('business.description.third.partTwo')}</b>
      </DescriptionWithIcon>{' '}
      <DescriptionWithIcon color={'var(--vg-colors-upgrade-blue-500)'}>
        {t('business.description.fourth.partOne')} <b>{t('business.description.fourth.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-upgrade-blue-500)'}>
        {t('general.description.fourth.partOne')} <b>1000 {t('general.description.fourth.partTwo')}</b>
      </DescriptionWithIcon>
      <DescriptionWithIcon color={'var(--vg-colors-upgrade-blue-500)'}>
        <b>{t('business.description.sixth.partOne')}</b> {t('business.description.sixth.partTwo')}
      </DescriptionWithIcon>
    </VStack>
  );
};

// Below are shared components between premium and business components
const UpgradeButton = ({ onClickUpgrade, upgradePlan, modalType }: UpgradeButtonProps) => {
  const { t } = useTranslation('editor_modals', {
    useSuspense: false,
    keyPrefix: handleTranslationText(modalType),
  });

  const isUpgradingToPremium = upgradePlan === UserTypes.Premium;
  const isUpgradingToBusiness = upgradePlan === UserTypes.Business;

  const planName = isUpgradingToPremium && !isUpgradingToBusiness ? t('premium.planName') : t('business.planName');
  const buttonVariant =
    isUpgradingToPremium && !isUpgradingToBusiness ? 'icon-upgrade-premium' : 'icon-upgrade-business';
  const buttonText = t('general.upgradeButtonLabel', { planName });

  return (
    <Button
      variant={buttonVariant}
      width='100%'
      height='fit-content'
      paddingLeft={3}
      paddingRight={3}
      paddingTop={2.5}
      paddingBottom={2.5}
      gap={2}
      aria-label={buttonText}
      onClick={onClickUpgrade}
      alignSelf={'end'}
      marginTop={'auto'}
      data-testid={'icon-upgrade-confirm-button'}
    >
      <CrownIcon />
      <Text fontSize={14} fontWeight={'500'} height={4}>
        {buttonText}
      </Text>
    </Button>
  );
};

const Header = ({ upgradePlan, modalType }: HeaderProps) => {
  const { t } = useTranslation('editor_modals', {
    useSuspense: false,
    keyPrefix: handleTranslationText(modalType),
  });

  const isUpgradingToPremium = upgradePlan === UserTypes.Premium;
  const isUpgradingToBusiness = upgradePlan === UserTypes.Business;

  const planName = isUpgradingToPremium && !isUpgradingToBusiness ? t('premium.planName') : t('business.planName');
  const planLabelColor =
    isUpgradingToPremium && !isUpgradingToBusiness ? 'var(--vg-colors-avatar-3)' : 'var(--vg-colors-upgrade-blue-500)';

  const showPlanName = !(
    modalType === ModalUpgradeTypeEnum.FontKit ||
    modalType === ModalUpgradeTypeEnum.ImproveText ||
    modalType === ModalUpgradeTypeEnum.BrandKit ||
    modalType === ModalUpgradeTypeEnum.Folder ||
    (modalType === ModalUpgradeTypeEnum.Templates && isUpgradingToBusiness) ||
    modalType === ModalUpgradeTypeEnum.BackgroundRemover ||
    modalType === ModalUpgradeTypeEnum.Elements
  );
  const isBusinessTemplates = modalType === ModalUpgradeTypeEnum.Templates && isUpgradingToBusiness;

  return (
    <ModalHeader display='inline-flex' alignItems='center' maxH={''} padding={'unset'}>
      <CustomText fontSize={24} color={'font.500'}>
        {isBusinessTemplates ? <>{t('general.header.partOneBusiness')}</> : <>{t('general.header.partOne')}</>}{' '}
        {showPlanName && (
          <CustomText fontSize={24} color={planLabelColor}>
            {planName}{' '}
          </CustomText>
        )}
        {showPlanName && <>{t('general.header.partThree')}</>}
      </CustomText>
    </ModalHeader>
  );
};

const Subheader = ({ upgradePlan, modalType }: SubheaderProps) => {
  const { t } = useTranslation('editor_modals', {
    useSuspense: false,
    keyPrefix: handleTranslationText(modalType),
  });

  const isUpgradingToPremium = upgradePlan === UserTypes.Premium;
  const isUpgradingToBusiness = upgradePlan === UserTypes.Business;

  const planName = isUpgradingToPremium && !isUpgradingToBusiness ? t('premium.planName') : t('business.planName');

  return (
    <>
      {modalType === ModalUpgradeTypeEnum.ImproveText ? (
        <Description lineHeight={'20px'}>
          {t('general.subheader.first.partOne')}{' '}
          <b>{t('general.subheader.first.partTwo', { maxCredits: MAX_TEXT_GENERATION_CREDITS })}</b>{' '}
          {t('general.subheader.first.partThree')} <b>{t('general.subheader.first.partFour')}</b>
        </Description>
      ) : (
        <Description lineHeight={'20px'}>
          {t('general.subheader.first.partOne')} <b>{planName}</b> {t('general.subheader.first.partThree')}
        </Description>
      )}
      <Description lineHeight={'125.5%'} fontWeight={'400'}>
        {t('general.subheader.second.partOne', {
          planName,
        })}
      </Description>
    </>
  );
};

const Description = ({ lineHeight = '115.5%', fontWeight = '400', children }: DescriptionProps) => {
  return (
    <Text
      fontSize={14}
      fontFamily={'Inter'}
      fontStyle={'normal'}
      fontWeight={fontWeight}
      lineHeight={lineHeight}
      color={'font.500'}
      textAlign={'left'}
      justifyContent={'center'}
    >
      {children}
    </Text>
  );
};

const CustomText = ({ fontSize, color, children }: CustomTextProps) => {
  return (
    <Text
      fontSize={fontSize}
      fontFamily='Inter'
      fontStyle='normal'
      fontWeight='700'
      lineHeight={'normal'}
      color={color}
      textAlign='left'
      justifyContent='center'
      as={'span'}
      overflow={'auto'}
    >
      {children}
    </Text>
  );
};

const Icon = ({ color }: IconProps) => <CheckMarkIcon fill={color} />;

const DescriptionWithIcon = ({ color, children }: DescriptionWithIconProps) => {
  return (
    <HStack minH={'1rem'} h={'fit-content'} as='li'>
      <Box minW={'16px'} minH={'16px'}>
        <Icon color={color} />
      </Box>
      <Description>{children}</Description>
    </HStack>
  );
};
